import { useCallback, useEffect, useRef, useState } from 'react';

import { getRateItFeed, getRateItItem } from 'services/tiri';

const PAGE_SIZE = 10;

/**
 * The feed's items, assembled from the shared moment plus successive random draws.
 *
 * There is no react-query in this repo, so this is plain `useState` + refs — but the two
 * behaviours the mobile hook gets from react-query still have to exist here, and both are load
 * bearing:
 *
 *   1. DEDUPE BY `_id`. The endpoint is `$sample`-based with `nextCursor: null`: every call is
 *      an independent random draw, so two calls legitimately overlap. Without the `seen` set the
 *      fan would scroll past the same moment twice in one session.
 *   2. NEVER REFETCH BEHIND THE FAN. Pages are appended and never replaced, so an open feed's
 *      indices are stable. On mobile an automatic background refetch swapped the pages under the
 *      pager and jumped the fan to unrelated content mid-session; here it would also tear down
 *      and re-initialise the video player. New content only ever arrives via `loadMore`.
 *
 * The SHARED moment is fetched separately and prepended, with its id seeded into the `seen` set
 * so a later draw cannot show it a second time. That is what honours the brief's requirement
 * that the landing display the exact moment the referrer sent: the random draw will usually not
 * contain it.
 */
export default function useRateItFeed({ tiriId, teamId }) {
  const [items, setItems] = useState([]);
  const [sharedItem, setSharedItem] = useState(null);
  const [sharedRating, setSharedRating] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  /** `'shared'` — the shared moment is gone; `'feed'` — nothing at all could be loaded. */
  const [errorKind, setErrorKind] = useState(null);

  const seenRef = useRef(new Set());
  // Guards against a fast scroll firing three overlapping draws, which would waste two of them
  // and (because each is random) mostly return duplicates anyway.
  const isFetchingRef = useRef(false);

  /** Appends only the ids not already on screen. Returns how many were genuinely new. */
  const appendDraw = useCallback((drawItems) => {
    const fresh = (drawItems ?? []).filter((item) => !seenRef.current.has(item._id));
    fresh.forEach((item) => seenRef.current.add(item._id));
    if (fresh.length) setItems((prev) => [...prev, ...fresh]);
    return fresh.length;
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      seenRef.current = new Set();
      setItems([]);

      // The shared moment first and on its own, so a failure here can be reported without
      // costing the fan the rest of the feed.
      let shared = null;
      if (tiriId) {
        const res = await getRateItItem(tiriId);
        if (cancelled) return;

        if (res?.item) {
          shared = res.item;
          setSharedItem(res.item);
          // `canRate: false` with no rating means the viewer owns the moment; either way the bar
          // is locked and the reason is worth showing rather than silently disabling it.
          setSharedRating(res.myRating ?? null);
        } else {
          // The link outlived its moment (deleted, or moderation pulled it). Not fatal: the
          // referral is still valid and there are other moments to rate, so the feed continues
          // and the screen shows an inline note instead of dead-ending.
          setErrorKind('shared');
        }
      }

      if (shared) {
        seenRef.current.add(shared._id);
        setItems([shared]);
      }

      const res = await getRateItFeed({ limit: PAGE_SIZE, teamId });
      if (cancelled) return;

      if (!res?.items) {
        // Only an error if there is nothing on screen at all — with a shared moment in hand the
        // fan can still rate, which is the whole point of the landing.
        if (!shared) setErrorKind('feed');
        setHasNextPage(false);
      } else {
        appendDraw(res.items);
        setHasNextPage(res.items.length > 0);
      }

      setIsLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [tiriId, teamId, appendDraw]);

  const loadMore = useCallback(async () => {
    if (isFetchingRef.current || !hasNextPage) return;
    isFetchingRef.current = true;
    setIsFetchingNextPage(true);

    const res = await getRateItFeed({ limit: PAGE_SIZE, teamId });
    const added = appendDraw(res?.items);

    // `hasNextPage` is DERIVED, not reported by the server: a draw that yields no item this fan
    // has not already seen means the pool is exhausted for them. Without this the trailing
    // skeleton would sit there forever at the end of a small team's feed.
    if (!res?.items?.length || added === 0) setHasNextPage(false);

    setIsFetchingNextPage(false);
    isFetchingRef.current = false;
  }, [teamId, hasNextPage, appendDraw]);

  return {
    items,
    sharedItem,
    sharedRating,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    errorKind,
    loadMore
  };
}
